import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { List, Map } from 'immutable';

import BetTickerForm from '../BetTickerForm';
import VirtualizedTable from '../VirtualizedTable';

import {
    matchStakeFactor,
    COLUMNS,
    DEFAULT_HIGHLIGHT_COLOR,
    DEFAULT_HIGHLIGHT_COLOR_2,
    DEFAULT_HIGHLIGHT_COLOR_3,
    DEFAULT_HIGHLIGHT_COLOR_4,
    DEFAULT_HIGHLIGHT_COLOR_5,
    DEFAULT_HIGHLIGHT_COLOR_6,
    DEFAULT_HIGHLIGHT_COLOR_7,
} from './config.jsx';

const BET_TICKER_PRESETS_ALL = {
    allBets: {
        label: 'All bets',
        rules: {},
    },
    allBets25: {
        label: 'All bets £25+',
        rules: {
            betsAbove: 25,
        },
    },
    singleBets: {
        label: 'Single bets',
        rules: {
            betType: 'SGL',
        },
    },
    desktopBets: {
        label: 'Desktop bets',
        rules: {
            channel: 'desktop',
        },
    },
    horseRacing: {
        label: 'All Horse Racing',
        rules: {
            sport: [{ id: 'horseracing', competitions: true }],
        },
    },
    football: {
        label: 'All Football',
        rules: {
            sport: [{ id: 'football', competitions: true }],
        },
    },
    restrictedClients: {
        label: 'Restricted Clients',
        rules: {
            stakeFactor: 1,
            stakeFactorCondition: '<',
        },
    },
    betsFCTC: {
        label: 'Greyhound FC/TC',
        rules: {
            betsType: ['FC', 'RFC', 'CFC', 'TC', 'CTC'],

            sport: [{ id: 'greyhoundracing', competitions: true }],
        },
    },

    custom: {
        label: 'Custom',
    },

    allRejectedBets: {
        label: 'All Rejected Bets',
        rules: {
            rejectedBets: true,
        },
    },

    freebets: {
        label: 'All Free Bets',
        rules: {
            freebet: true,
        },
    },

    cashOutBets: {
        label: 'Cash Out Bets',
        rules: {
            cashOutBets: true,
        },
    },
};

const get_bet_ticker_presets = () => {
    const BET_TICKER_PRESETS = { ...BET_TICKER_PRESETS_ALL };

    return BET_TICKER_PRESETS;
};

class BetTickerBox extends React.Component {
    static propTypes = {
        competitions: ImmutablePropTypes.list,
        sports: ImmutablePropTypes.list,
        data: ImmutablePropTypes.map,
    };

    static defaultProps = {
        competitions: List(),
        sports: List(),
        data: Map(),
    };

    constructor(props, context) {
        super(props, context);

        this.state = {
            rules: null,
            custom: false,
        };

        this.splitRow = this.splitRow.bind(this);
    }

    selectPreset(preset, e) {
        if (e) {
            e.preventDefault();
        }

        this.setState({
            preset: get_bet_ticker_presets()[preset].label,
            rules: get_bet_ticker_presets()[preset].rules,
        });
    }

    selectRules(rules) {
        this.setState({
            preset: 'Custom',
            rules,
            custom: false,
        });
    }

    close(e) {
        if (e) {
            e.preventDefault();
        }

        this.setState({
            preset: null,
            rules: null,
            custom: false,
        });
    }

    openCustom(e) {
        if (e) {
            e.preventDefault();
        }

        this.setState({
            custom: true,
        });
    }

    splitRow(bet) {
        if (bet.legs.length === 1) {
            return [bet];
        }

        return bet.legs.map((leg, index) => {
            const row = Object.assign({}, bet);
            row.legs = [leg];

            if (index) {
                delete row.placedAt;
                // delete row.account;
                delete row.stakeFactor;
                delete row.eachWay;
                // delete row.type;
                delete row.stakePerLine;
                delete row.totalStake;
                delete row.payout;
                delete row.maxBet;
                delete row.channel;
                delete row.eachWay;
            }

            return row;
        });
    }

    render() {
        const { sports, competitions, data: initialData } = this.props;
        const { rules, preset, custom } = this.state;
        let content;

        if (rules) {
            const data = initialData.get(rules.rejectedBets ? 'rejected' : rules.cashOutBets ? 'settled' : 'accepted') || Map();
            let sportsLabel;
            if (rules.sport && rules.sport.length) {
                sportsLabel = rules.sport
                    .map((s) => {
                        let label = `${sports.find((x) => x.get('id') === s.id).get('name') } (`;

                        if (s.competitions === true) {
                            label += 'All';
                        } else if (s.competitions.length === 1) {
                            label += competitions.find((x) => x.get('id') === s.competitions[0]).get('name');
                        } else {
                            label += s.competitions.length;
                        }

                        return `${label })`;
                    })
                    .join(', ');
            } else {
                sportsLabel = 'All';
            }

            const settingsLabel = [];
            if (rules.betsAbove) {
                settingsLabel.push(`Bets above £${ parseFloat(rules.betsAbove).toFixed(2)}`);
            }

            if (rules.stakeFactor) {
                settingsLabel.push(`Stake factor ${ rules.stakeFactorCondition } ${ rules.stakeFactor}`);
            }

            const filterBySum = (elem) =>
                rules.betsAbove ? elem.toJS().totalStake > parseFloat(rules.betsAbove).toFixed(2) * 100 : elem;

            const filterByBetType = (elem) => (rules.betsType ? rules.betsType.indexOf(elem.toJS().type) !== -1 : elem);

            const filterByStakeFactor = (elem) =>
                rules.stakeFactor
                    ? matchStakeFactor(elem.toJS().stakeFactor, {
                          'highlight.stakeFactorCondition': rules.stakeFactorCondition,
                          'highlight.stakeFactor': rules.stakeFactor,
                      })
                    : elem;

            const filterBySport = (elem) =>
                rules.sport && rules.sport.length > 0
                    ? elem.toJS().legs.find((leg) =>
                          rules.sport.find((y) => {
                              return Array.isArray(y.competitions)
                                  ? y.id === leg.sport.id &&
                                        y.competitions.indexOf(parseInt(leg.competition.id, 10)) >= 0
                                  : y.id === leg.sport.id;
                          })
                      )
                    : elem;

            // const filterDuplicates = bet => {
            //     // Get bet id to remove duplicates
            //     const betId = bet.get('id');
            //
            //     // Register unique bets
            //     if (betIds.indexOf(betId) === -1) {
            //         betIds.push(betId);
            //
            //         return true;
            //     }
            //
            //     return false;
            // };

            // let betIds = [];
            let filteredData = data
                .toList()
                .filter((x) => filterBySum(x))
                .filter((y) => filterBySport(y))
                .filter((z) => filterByStakeFactor(z))
                .filter((b) => filterByBetType(b));

            // Remove Reason column if current view is not displaying rejected bets
            let betStateColumns = COLUMNS;
            if (!rules.rejectedBets) {
                betStateColumns = COLUMNS.filter((col) => col.name !== 'rejectReason');
                //
                //     // Filter duplicates only for accepted bets because of status change
                //     // from open to settled
                //     filteredData = filteredData.filter(b => filterDuplicates(b));
            }

            if (rules.freebet) {
                filteredData = filteredData.filter((bet) => bet.get('freebet'));
            }

            if (rules.betType !== undefined) {
                filteredData = filteredData.filter((bet) => bet.get('type') === rules.betType);
            }

            if (rules.channel !== undefined) {
                filteredData = filteredData.filter((bet) => bet.get('channel') === rules.channel);
            }

            if (rules.cashOutBets === undefined) {
               betStateColumns = COLUMNS.filter((col) => !['payout', 'settledAt'].includes(col.name));
               filteredData = filteredData.filter((bet) => bet.get('status') !== 'settled');
            }

            if (rules.cashOutBets) {
                filteredData = filteredData.filter((bet) => bet.get('cashOut'));
            }

            filteredData = filteredData
                .sortBy((x) => x.get(rules.cashOutBets === undefined ? 'placedAt' : 'settledAt' ))
                .reverse()
                .toJS()
                .reduce((acc, row) => acc.concat(this.splitRow(row)), []);

            content = (
                <div className='bet-ticker-box__wrap'>
                    <a href='#' className='bet-ticker-box-close' onClick={this.close.bind(this)}>
                        &times;
                    </a>

                    <ul className='bet-ticker-box-settings'>
                        <li data-test='ticker-box-settings-title'>
                            <strong>Title:</strong> {preset}
                        </li>
                        {(settingsLabel.length > 0) ? (
                            <li data-test='ticker-box-settings'>
                                <strong>Settings:</strong> {settingsLabel.join(', ')}
                            </li>
                        ) : (
                            void 0
                        )}
                        <li data-test='ticker-box-settings-sports'>
                            <strong>Sports:</strong> {sportsLabel}
                        </li>
                    </ul>

                    <VirtualizedTable columns={betStateColumns} rows={filteredData} config={rules} />
                </div>
            );
        } else if (custom) {
            content = (
                <div className='bet-ticker-box__wrap'>
                    <a
                        href='#'
                        className='bet-ticker-box-close bet-ticker-box-close--form'
                        onClick={this.close.bind(this)}
                    >
                        &times;
                    </a>

                    <div className='bet-ticker-box__form'>
                        <BetTickerForm
                            onSubmit={this.selectRules.bind(this)}
                            competitions={competitions}
                            data={{
                                sport: [],
                                stakeFactorCondition: '<=',
                                'highlight.stakeFactorCondition': '<=',
                                'highlight.betsAboveColor': DEFAULT_HIGHLIGHT_COLOR,
                                'highlight.stakeFactorColor': DEFAULT_HIGHLIGHT_COLOR_2,
                                'highlight.channelDesktopColor': DEFAULT_HIGHLIGHT_COLOR_3,
                                'highlight.channelMobileColor': DEFAULT_HIGHLIGHT_COLOR_4,
                                'highlight.channelQuickbetColor': DEFAULT_HIGHLIGHT_COLOR_5,
                                'highlight.channelTelebettingColor': DEFAULT_HIGHLIGHT_COLOR_6,
                                'highlight.maxStakePercentageColor': DEFAULT_HIGHLIGHT_COLOR_7,
                            }}
                            sports={sports}
                        />
                    </div>
                </div>
            );
        } else {
            content = (
                <ul className='bet-ticker-box-presets' data-test='bet-ticker-bets-listview'>
                    {Object.keys(get_bet_ticker_presets()).map((p) => (
                        <li key={p}>
                            <a
                                href='#'
                                onClick={p === 'custom' ? this.openCustom.bind(this) : this.selectPreset.bind(this, p)}
                            >
                                {get_bet_ticker_presets()[p].label}
                            </a>
                        </li>
                    ))}
                </ul>
            );
        }

        return <div className='bet-ticker-box'>{content}</div>;
    }
}

export default BetTickerBox;
